import { Button, Col, Flex, Input, message, Row, Space, Table, Tag, theme, Typography, Tooltip, Dropdown, Modal } from "antd";
import React, { useEffect, useState } from "react";
import { useDebouncedSearch } from "../../../../hooks.js";
import { DownloadOutlined, EllipsisOutlined, ExclamationCircleFilled, GlobalOutlined, PlusOutlined } from "@ant-design/icons";
import CreateLegalDocumentModalEditor from "./CreateLegalDocumentModalEditor.jsx";
import CreateLegalDocumentModalUpload from "./CreateLegalDocumentModalUpload.jsx";
import { downloadLegalDocument, getAllLegalDocuments, postLegalDocument, deletehLegalDocAjax, publishLegalDocAjax, updateLegalDocumentName, updateLegalDocumentValues, updateLegalDocumentVersion } from "../../../../api/preferences.js";
import dayjs from "dayjs";
import EditDocumentModalEditor from "./EditDocumentModalEditor.jsx";
import EditDocumentModalUpload from "./EditDocumentModalUpload.jsx";
import AddDocumentModal from "./AddDocumentModal.jsx";
import Loading from "../../../common/Loading.js.jsx";
import { useSelector } from "react-redux";
import SVG from "react-inlinesvg";

const { Text, Paragraph } = Typography;

const getHighlightedText = (text, highlight) => {
  let parts = []
  if (text) {
    parts = text.split(new RegExp(`(${highlight})`, 'gi'));
  }
  return parts.map((part, index) =>
    part.toLowerCase() === highlight.toLowerCase() ? (
      <Text key={index} mark>{part}</Text>
    ) : part
  );
};

const LegalDocumentManagementHome = (props) => {
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [allLegalDocs, setAllLegalDocs] = useState([]);
  const [dataSource, setDataSource] = useState([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [openCreateDocumentModalEditor, setOpenCreateDocumentModalEditor] = useState(false);
  const [openCreateDocumentModalUpload, setOpenCreateDocumentModalUpload] = useState(false);
  const [openEditModalEditor, setOpenEditModalEditor] = useState(false);
  const [openEditModalUpload, setOpenEditModalUpload] = useState(false);
  const [docTypeGroup, setDocTypeGroup] = useState(null);
  const [documentToEdit, setDocumentToEdit] = useState(null);
  const [showAddDocumentModal, setShowAddDocumentModal] = useState(false);
  const { token } = theme.useToken();
  const debouncedChangeHandler = useDebouncedSearch(setSearchValue, 700);
  const [modal, contextHolder] = Modal.useModal();
  const currentUser = useSelector(state => state.accountState.getIn(['userDetails', 'value']));
  const sites = useSelector(state => state.siteState.getIn(['sites', 'value'])).toJS();

  const handleAddNew = (docTypeGroup) => {
    docTypeGroup.hasContent ? setOpenCreateDocumentModalEditor(true) : setOpenCreateDocumentModalUpload(true);
    if (docTypeGroup) {
      const docGroup = allLegalDocs.find((docGroup) => docGroup.documentName === docTypeGroup?.documentName);
      setDocTypeGroup((docs) => ({
        ...docTypeGroup,
        documents: docGroup?.documents ?? docs.children
      }));
    } else {
      setDocTypeGroup(null);
    }
  };
  
  const handleDownload = async (record) => {
    const documentId = record._id;
    try {
      setLoading(true);
      const blob = await downloadLegalDocument(documentId, 'upload');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = record.versionName ?? `document-${documentId}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setLoading(false);
      message.success(<>Your document <b>{record.versionName}</b> has been successfully downloaded</>);
    } catch (err) {
      message.error(err.message || "Something went wrong");
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    const docGroup = allLegalDocs.find(doc => doc.documentName === record.documentName);
    setDocTypeGroup(docGroup ?? null);
    setDocumentToEdit(record);
    record.hasContent ? setOpenEditModalEditor(true) : setOpenEditModalUpload(true);
  };

  const handleDelete = async (doc) => {
    const confirmation = await modal.confirm({
      title: "Document - Delete",
      content: <>Are you sure you want to delete version <b>{doc.versionName}</b> in {doc?.documentName}?</>,
      okText: "Delete",
      cancelText: "Cancel",
      width: 542
    });
    if (confirmation) {
      try {
        setLoading(true);
        await deletehLegalDocAjax(doc._id);
        fetchAllDocs();
        message.success(<>Version <b>{doc.versionName}</b> deleted</>);
      } catch (err) {
        message.error(err.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePublish = async (documentId) => {
    const confirmation = await modal.confirm({
      title: 'Publish Document',
      icon: <ExclamationCircleFilled style={{ color: token.colorPrimary }} />,
      content: (
        <>
          Please confirm you want to publish this document.
          <ul>
            <li>It may trigger reconsent messages.</li>
            <li>It will update associations in Preference messages.</li>
          </ul>
        </>
      ),
      okText: 'Publish',
      cancelText: 'Cancel',
      width: 542
    });
    if (confirmation) {
      try {
        setLoading(true);
        await publishLegalDocAjax(documentId);
        fetchAllDocs();
        message.success("Document published");
      } catch (err) {
        message.error(err.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    }
  };

  const columns = [
    {
      title: 'Doc Name',
      dataIndex: 'documentName',
      key: 'documentName',
      filters: [
        { text: 'Has Content', value: 'hasContent' }
      ],
      onFilter: (value, record) => {
        if (value === 'hasContent') {
          return record.hasContent === true;
        }
        return true;
      },
      render: (text, record) => record.isGroupRow ? text : null,
    },
    {
      title: 'Version Name',
      dataIndex: 'versionName',
      key: 'versionName',
      render: (text, record) => getHighlightedText(text, searchValue.trim())
    },
    {
      title: 'Version ID',
      dataIndex: '_id',
      key: '_id',
      render: (text, record) => getHighlightedText(text, searchValue.trim())
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        switch (status) {
          case 'LIVE': return <Tag color="green"><SVG src="/svg_icons/Ellipse.svg" className="ellipse-icon green-8" /> Live</Tag>;
          case 'PREV_LIVE': return <Tag>Prev. Live</Tag>;
          case 'DRAFT': return <Tag color="blue">Draft</Tag>;
          default: return null;
        }
      }
    },
    {
      title: 'Published On',
      dataIndex: 'publishedAt',
      key: 'publishedAt',
      render: (publishedAt) => publishedAt ? dayjs(publishedAt).format('MMMM D, YYYY, h:mm A') : null
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        if (record.isGroupRow) {
          return (
            <Button
              type="link"
              onClick={() => { handleAddNew(record) }}
              icon={<PlusOutlined />}
            >
              Version
            </Button>
          );
        }

        const isDraft = record.status === 'DRAFT';

        let dropdownItems = [];
        if (isDraft) {
          dropdownItems = [
            {
              key: 'edit',
              label: <div onClick={() => handleEdit(record)}>Edit</div>
            },
            {
              key: 'download',
              label: <div onClick={() => handleDownload(record)}>Download</div>
            },
            {
              key: 'delete',
              danger: true,
              label: <div onClick={() => handleDelete(record)}>Delete</div>
            }
          ];
        } else if (record.hasContent) {
          dropdownItems = [
            {
              key: 'edit',
              label: <div onClick={() => handleEdit(record)}>View</div>
            },
          ];
        }

        return (
          <Row align="middle">
            <Col span={18}>
              {isDraft ? (
                <Button
                  type="link"
                  icon={<GlobalOutlined />}
                  onClick={() => handlePublish(record._id)}
                >
                  Publish
                </Button>
              ) : (
                <Button
                  type="link"
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownload(record)}
                >
                  Download
                </Button>
              )}
            </Col>
            <Col span={6} style={{ display: 'flex', justifyContent: 'space-between' }}>
              {dropdownItems.length > 0 && (
                <Dropdown
                  placement="bottomRight"
                  arrow
                  menu={{ items: dropdownItems }}
                  trigger={['click']}
                >
                  <EllipsisOutlined />
                </Dropdown>
              )}
            </Col>
          </Row>
        );
      }
    }
  ];

  const fetchAllDocs = async () => {
    try {
      setLoading(true);
      let docs = await getAllLegalDocuments();
      docs = docs.map(group => ({
        ...group,
        
        documents: group.documents
          .map(doc => ({ ...doc, status: doc.status ?? "DRAFT" }))
          .sort((a, b) => {
            if (a.status === 'LIVE') return -1;
            if (b.status === 'LIVE') return 1;
            return dayjs(b.dateCreated).unix() - dayjs(a.dateCreated).unix();
          })
      }));
      setAllLegalDocs(docs);
      setExpandedRowKeys(docs.map(doc => doc._id || doc.containerId));
      setLoading(false);
    } catch (err) {
      message.error(err.message || "Something went wrong");
      setLoading(false);
    }
  };

  const handleSaveDocument = async (formData, isNew, publish) => {
    let data = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        data.append(key, value);
      }
    });
  
    data.append("accountId", currentUser.accountId);
  
    let res;
    try {
      setLoading(true);
      if (isNew) {
        res = await postLegalDocument(data);
        if (publish) await publishLegalDocAjax(res._id);
        fetchAllDocs();
        message.success(<>New Document <b>{formData.documentName}</b> saved successfully</>);
      } else {
        res = await updateLegalDocumentVersion(data);
        // should change responsse from returning all versions to just the updated one
        if (publish) await publishLegalDocAjax(res[Object.keys(res).at(-3)]._id);
        fetchAllDocs();
        message.success(<>New Document Version <b>{formData.versionName}</b> uploaded successfully</>);
      }
      setOpenCreateDocumentModalEditor(false);
      setOpenCreateDocumentModalUpload(false);
      setDocTypeGroup(null);
      setLoading(false);
      return res;
    } catch (err) {
      message.error(err.message || "Something went wrong");
      setLoading(false);
    }
  };  

  const handleEditDocument = async (formData, editDocName, publish) => {
    formData.accountId = currentUser.accountId;
    let res;
    try {
      setLoading(true);
      if (editDocName && formData.documentName !== documentToEdit.documentName) {
        await updateLegalDocumentName(
          { accountId: currentUser.accountId, documentName: formData.documentName },
          documentToEdit.documentName
        );
        res = await updateLegalDocumentValues(formData, documentToEdit._id);
        if (publish) await publishLegalDocAjax(res._id);
      } else {
        res = await updateLegalDocumentValues(formData, documentToEdit._id);
        if (publish) await publishLegalDocAjax(res._id);
      }
      fetchAllDocs();
      message.success("doc values updated");
      setOpenEditModalEditor(false);
      setOpenEditModalUpload(false);
      setDocTypeGroup(null);
      setDocumentToEdit(null);
      setLoading(false);
      return res;
    } catch (err) {
      message.error(err.message || "Something went wrong");
      setLoading(false);
    }
  };
  

  useEffect(() => { fetchAllDocs(); }, []);

  useEffect(() => {
    const rowKeys = [];
    const ds = allLegalDocs.map(group => {
      const children = group.documents
        .map(doc => ({
          ...doc,
          documentName: group.documentName,
          hasContent: group.hasContent
        }))
        .filter(doc => {
          if (!searchValue.trim()) return true;
          const match = doc._id?.toLowerCase().includes(searchValue.trim().toLowerCase())
            || doc.versionName?.toLowerCase().includes(searchValue.trim().toLowerCase())
            || group.documentName?.toLowerCase().includes(searchValue.trim().toLowerCase());
          if (match) rowKeys.push(group.documentName);
          return match;
        });

      return {
        key: group._id || group.containerId,
        documentName: group.documentName,
        isGroupRow: true,
        hasContent: group.hasContent,
        children
      };
    }).filter(row => !searchValue.trim() || row.children.length);

    setDataSource(ds);
  }, [searchValue, allLegalDocs]);

  return currentUser ? (
    <>
      <Row gutter={[0, token.marginMD]}>
        <Col span={24}>
          <Typography.Title level={2}>Legal Document Library</Typography.Title>
        </Col>
        <Col span={24}>
          <Space direction="vertical" size={token.marginXS}>
            <Typography.Text type="secondary">
              The Legal Documents Library is where you create a repository of your legal documents to keep a record of what documents have been disclosed to your users.
            </Typography.Text>
          </Space>
        </Col>
        <Col span={24}>
          <Flex justify="space-between" align="center">
            <Input.Search
              placeholder="Search by version name or ID"
              onChange={({ target: { value } }) => debouncedChangeHandler(value)}
              style={{ width: 360 }}
            />
            <Button type="primary" onClick={() => setShowAddDocumentModal(true)}>+ New Doc</Button>
          </Flex>
        </Col>
        <Col span={24}>
          <Table
            columns={columns}
            dataSource={dataSource}
            expandable={{
              expandedRowKeys,
              onExpand: (expanded, record) => {
                setExpandedRowKeys(expanded
                  ? [...expandedRowKeys, record.key]
                  : expandedRowKeys.filter(k => k !== record.key)
                );
              }
            }}
            loading={loading}
            pagination={false}
            scroll={{ y: `calc(100vh - 350px)` }}
          />
        </Col>
      </Row>

      {showAddDocumentModal && (
        <AddDocumentModal
          open={showAddDocumentModal}
          onCancel={() => setShowAddDocumentModal(false)}
          handleAddNewWithUpload={() => setOpenCreateDocumentModalUpload(true)}
          handleAddNewWithEditor={() => setOpenCreateDocumentModalEditor(true)}
        />
      )}
      {openCreateDocumentModalEditor && (
        <CreateLegalDocumentModalEditor
          loading={loading}
          open={openCreateDocumentModalEditor}
          onCancel={() => {setOpenCreateDocumentModalEditor(false);setDocTypeGroup(null)}}
          docTypeGroup={docTypeGroup}
          handleSaveDocument={handleSaveDocument}
          documentNames={allLegalDocs.filter((doc) => doc.documentName !== docTypeGroup?.documentName).map((doc) => doc.documentName)}
          sites={sites}
          features={currentUser?.accountFeatures}
        />
      )}
      {openCreateDocumentModalUpload && (
        <CreateLegalDocumentModalUpload
          loading={loading}
          open={openCreateDocumentModalUpload}
          onCancel={() => {setOpenCreateDocumentModalUpload(false);setDocTypeGroup(null)}}
          docTypeGroup={docTypeGroup}
          handleSaveDocument={handleSaveDocument}
          documentNames={allLegalDocs.filter((doc) => doc.documentName !== docTypeGroup?.documentName).map((doc) => doc.documentName)}
        />
      )}
      {openEditModalEditor && (
        <EditDocumentModalEditor
          loading={loading}
          open={openEditModalEditor}
          onCancel={() => {setOpenEditModalEditor(false);setDocTypeGroup(null);setDocumentToEdit(null)}}
          docTypeGroup={docTypeGroup}
          documentToEdit={documentToEdit}
          handleSaveDocument={handleEditDocument}
          documentNames={allLegalDocs.filter((doc) => doc.documentName !== documentToEdit?.documentName).map((doc) => doc.documentName)}
          sites={sites}
          features={currentUser?.accountFeatures}
        />
      )}
      {openEditModalUpload && (
        <EditDocumentModalUpload
          loading={loading}
          open={openEditModalUpload}
          onCancel={() => {setOpenEditModalUpload(false);setDocTypeGroup(null);setDocumentToEdit(null)}}
          docTypeGroup={docTypeGroup}
          documentToEdit={documentToEdit}
          handleSaveDocument={handleEditDocument}
          documentNames={allLegalDocs.filter((doc) => doc.documentName !== documentToEdit?.documentName).map((doc) => doc.documentName)}
        />
      )}
      {contextHolder}
    </>
  ) : <Loading />;
};

export default LegalDocumentManagementHome;
